import { getAuthToken } from "../../../auth/authClient";

const DEFAULT_TIMEOUT_MS = 30000;

const getFourEBaseUrl = () => {
  const envUrl = process.env.REACT_APP_MORFEUSZ_4E_API_URL;
  return String(envUrl || "").trim().replace(/\/$/, "");
};

const getMorfeuszNonce = () => {
  if (typeof window === "undefined") return "";
  try {
    if (typeof window.morfeuszNonce === "function") return String(window.morfeuszNonce() || "");
    const field = document.getElementById("morfeusz_nonce");
    return String(field?.value || field?.getAttribute?.("value") || "");
  } catch (_) {
    return "";
  }
};

const requestHeaders = (extra = {}) => {
  const token = getAuthToken();
  const nonce = getMorfeuszNonce();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(nonce ? { "X-Morfeusz-Nonce": nonce } : {}),
    ...extra,
  };
};

const withTimeout = async (promise, timeoutMs = DEFAULT_TIMEOUT_MS) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => {
      reject(new Error("request_timeout"));
    }, timeoutMs);
  });

  try {
    return await Promise.race([promise, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const normalizeFetchError = (error) => ({
  ok: false,
  code: error?.message === "request_timeout" ? "request_timeout" : "network_error",
  message: error?.message === "request_timeout"
    ? "Backend 4E nie odpowiedział na czas."
    : "Nie udało się połączyć z backendem 4E.",
});

const getJson = async (path) => {
  const baseUrl = getFourEBaseUrl();

  if (!baseUrl) {
    return {
      ok: false,
      code: "not_configured",
      message: "Backend 4E nie jest jeszcze skonfigurowany.",
    };
  }

  try {
    const response = await withTimeout(fetch(`${baseUrl}${path}`, {
      method: "GET",
      credentials: "include",
      headers: requestHeaders(),
    }), 10000);

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        ok: false,
        code: data?.code || "backend_error",
        message: data?.message || "Backend 4E zwrócił błąd.",
        status: response.status,
      };
    }

    return data || {
      ok: false,
      code: "empty_response",
      message: "Backend 4E zwrócił pustą odpowiedź.",
    };
  } catch (error) {
    return normalizeFetchError(error);
  }
};

const postJson = async (path, payload, timeoutMs = DEFAULT_TIMEOUT_MS) => {
  const baseUrl = getFourEBaseUrl();

  if (!baseUrl) {
    return {
      ok: false,
      code: "not_configured",
      message: "Backend 4E nie jest jeszcze skonfigurowany.",
    };
  }

  try {
    const response = await withTimeout(fetch(`${baseUrl}${path}`, {
      method: "POST",
      headers: requestHeaders({
        "Content-Type": "application/json",
      }),
      credentials: "include",
      body: JSON.stringify(payload),
    }), timeoutMs);

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        ok: false,
        code: data?.code || "backend_error",
        message: data?.message || "Backend 4E zwrócił błąd.",
        status: response.status,
      };
    }

    return data || {
      ok: false,
      code: "empty_response",
      message: "Backend 4E zwrócił pustą odpowiedź.",
    };
  } catch (error) {
    return normalizeFetchError(error);
  }
};

const deleteJson = async (path) => {
  const baseUrl = getFourEBaseUrl();

  if (!baseUrl) {
    return {
      ok: false,
      code: "not_configured",
      message: "Backend 4E nie jest jeszcze skonfigurowany.",
    };
  }

  try {
    const response = await withTimeout(fetch(`${baseUrl}${path}`, {
      method: "DELETE",
      credentials: "include",
      headers: requestHeaders(),
    }), 10000);

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      return {
        ok: false,
        code: data?.code || "backend_error",
        message: data?.message || "Backend 4E zwrócił błąd.",
        status: response.status,
      };
    }

    return data || { ok: true };
  } catch (error) {
    return normalizeFetchError(error);
  }
};

export const fourEClient = Object.freeze({
  isConfigured: () => Boolean(getFourEBaseUrl()),
  status: () => getJson("/status"),
  limits: () => getJson("/4e/limits"),
  analyzeDream: (payload) => postJson("/4e/analyze", payload, 90000),
  followup: (payload) => postJson("/4e/followup", payload, 45000),
  profileStatus: () => getJson("/profile/status"),
  profileGet: () => getJson("/profile"),
  profileSave: (payload) => postJson("/profile", payload, 15000),
  profileExport: () => getJson("/profile/export"),
  profileConsent: (payload) => postJson("/profile/consent", payload, 10000),
  profileExtract: (payload) => postJson("/profile/extract", payload, 45000),
  profileDelete: () => deleteJson("/profile"),
});

export const FOUR_E_API_STATUS = Object.freeze({
  NOT_CONFIGURED: "not_configured",
  BACKEND_ERROR: "backend_error",
  NETWORK_ERROR: "network_error",
  REQUEST_TIMEOUT: "request_timeout",
});
